import React from "react";
import { Link } from "react-router-dom";
import { Clock, ArrowUpRight } from "lucide-react";
import { CATEGORY_META, formatDate } from "@/lib/constants";

export function CategoryBadge({ category, className = "" }) {
  const meta = CATEGORY_META[category];
  if (!meta) return null;
  return (
    <span className={`inline-block text-[10px] md:text-[11px] font-bold tracking-widest uppercase text-white px-2 py-1 ${className}`}
      style={{ backgroundColor: meta.color }} data-testid={`badge-${category}`}>
      {meta.label}
    </span>
  );
}

// variant: "grid" | "hero" | "list" | "compact"
export default function NewsCard({ article, variant = "grid" }) {
  if (!article) return null;
  const to = `/${article.category}/${article.slug}`;
  const meta = CATEGORY_META[article.category];

  if (variant === "compact") {
    return (
      <Link to={to} data-testid={`card-compact-${article.slug}`}
        className="group flex gap-3 py-3 border-b border-gray-200 last:border-0">
        <div className="flex-1 min-w-0">
          <CategoryBadge category={article.category} className="mb-1.5" />
          <h4 className="font-serif font-bold text-[15px] leading-snug text-nx-dark group-hover:text-nx-blue transition-colors line-clamp-3">
            {article.title}
          </h4>
          <div className="mt-1.5 text-[11px] text-gray-500 flex items-center gap-2">
            <span>{formatDate(article.date)}</span>
            <span className="flex items-center gap-1"><Clock size={11} />{article.reading_time}′</span>
          </div>
        </div>
        {article.image && (
          <div className="w-20 h-20 shrink-0 overflow-hidden rounded-sm">
            <img src={article.image} alt={article.title} loading="lazy"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          </div>
        )}
      </Link>
    );
  }

  if (variant === "hero") {
    return (
      <Link to={to} data-testid={`card-hero-${article.slug}`}
        className="group relative block h-[420px] md:h-[560px] overflow-hidden rounded-md">
        <img src={article.image} alt={article.title} loading="eager"
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/45 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
          <div className="flex items-center gap-2 mb-4">
            <CategoryBadge category={article.category} />
            {article.breaking && (
              <span className="text-[10px] font-bold tracking-widest uppercase bg-red-600 text-white px-2 py-1">Última hora</span>
            )}
          </div>
          <h2 className="font-serif font-black text-white text-3xl md:text-5xl leading-[1.05] max-w-4xl">
            {article.title}
          </h2>
          <p className="mt-4 text-slate-200 text-sm md:text-lg max-w-2xl line-clamp-2">{article.subtitle}</p>
          <div className="mt-5 flex items-center gap-3 text-xs md:text-sm text-slate-300">
            <span className="font-semibold text-white">{article.author}</span>
            <span>·</span><span>{formatDate(article.date)}</span>
            <span>·</span><span className="flex items-center gap-1"><Clock size={13} />{article.reading_time} min</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "list") {
    return (
      <Link to={to} data-testid={`card-list-${article.slug}`}
        className="group grid grid-cols-1 sm:grid-cols-[280px_1fr] gap-5 py-7 border-b border-gray-200">
        <div className="overflow-hidden rounded-sm h-48 sm:h-44">
          <img src={article.image} alt={article.title} loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        </div>
        <div>
          <CategoryBadge category={article.category} className="mb-3" />
          <h3 className="font-serif font-bold text-xl md:text-2xl leading-tight text-nx-dark group-hover:text-nx-blue transition-colors">
            {article.title}
          </h3>
          <p className="mt-2 text-gray-600 text-sm md:text-[15px] line-clamp-2">{article.subtitle}</p>
          <div className="mt-3 flex items-center gap-2.5 text-xs text-gray-500 flex-wrap">
            <span className="font-semibold text-gray-700">{article.author}</span>
            <span>·</span><span>{formatDate(article.date)}</span>
            <span>·</span><span className="flex items-center gap-1"><Clock size={12} />{article.reading_time} min lectura</span>
            {article.is_demo && <span className="text-[10px] uppercase tracking-wider bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">Demo</span>}
          </div>
        </div>
      </Link>
    );
  }

  // grid (default)
  return (
    <Link to={to} data-testid={`card-grid-${article.slug}`} className="group block">
      <article>
        <div className="overflow-hidden rounded-sm aspect-[16/10] mb-4">
          <img src={article.image} alt={article.title} loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        </div>
        <CategoryBadge category={article.category} className="mb-2.5" />
        <h3 className="font-serif font-bold text-lg md:text-xl leading-snug text-nx-dark group-hover:text-nx-blue transition-colors line-clamp-3">
          {article.title}
        </h3>
        <p className="mt-2 text-gray-600 text-sm line-clamp-2">{article.excerpt || article.subtitle}</p>
        <div className="mt-3 flex items-center gap-2 text-xs text-gray-500 flex-wrap">
          <span className="font-semibold text-gray-700">{article.author}</span>
          <span>·</span><span>{formatDate(article.date)}</span>
          <span className="flex items-center gap-1"><Clock size={12} />{article.reading_time}′</span>
        </div>
        <span className="mt-3 inline-flex items-center gap-1 text-[13px] font-bold uppercase tracking-wider group-hover:gap-2 transition-all"
          style={{ color: meta?.color }}>
          Leer más <ArrowUpRight size={15} />
        </span>
      </article>
    </Link>
  );
}
